import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input } from 'antd';
import { SplitShell } from '../layout/split/SplitShell';
import styles from '../layout/split/split.module.css';
import { ApiError, accountsApi, type LoginResponse, type MagicResponse } from '../api';

function isLoginResponse(r: LoginResponse | MagicResponse): r is LoginResponse {
  return 'redirect_to' in r;
}

type Mode = 'password' | 'magic';

/**
 * /login on accounts.fatherschool.com. Split-shell pattern:
 *   left  — Velora showcase (brand, headline, 4 module tiles, testimonial marquee)
 *   right — Welcome-back title + Google (Soon) + Password/Magic tabs + form
 *
 * Wired for real: submits to /api/accounts/login, which currently returns 503
 * with a friendly message ("not available yet") so the form shows a real error
 * rather than spinning. Real password + magic flow arrives with the identity
 * module (see PROGRESS.csv Accounts rows).
 */
export function LoginPage() {
  return (
    <SplitShell>
      <LoginForm />
    </SplitShell>
  );
}

function LoginForm() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setMagicSent(false);
  };

  async function submit(kind: Mode, e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (kind === 'password' && (!email || !password)) return;
    if (kind === 'magic' && !email) return;
    setBusy(true);
    setError(null);
    try {
      const res = await accountsApi.login(
        kind === 'magic' ? { email, magic: true } : { email, password },
      );
      if (kind === 'magic') {
        setMagicSent(true);
        return;
      }
      if (isLoginResponse(res)) {
        // The server's Set-Cookie header lands the session; full page nav so
        // the product host reads it on first request.
        window.location.href = res.redirect_to;
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (err.body as { message?: string } | null)?.message ?? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className={styles.stepHead}>
        <h2 className={styles.stepTitle}>Welcome back</h2>
        <p className={styles.stepSubtitle}>
          Sign in to your school's console. If someone at your school just invited you, use the link
          in your SMS instead.
        </p>
      </div>


      <div style={{ marginTop: 22 }}>
        <button type="button" className={styles.oauthButton} disabled>
          <GoogleMark />
          Continue with Google
          <span className={styles.pill}>Soon</span>
        </button>
      </div>

      <div className={styles.divider} style={{ margin: '22px 0' }}>OR</div>

      <div
        className={`${styles.tabs}${mode === 'magic' ? ` ${styles.tabsSecond}` : ''}`}
        role="tablist"
        aria-label="Sign-in method"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'password'}
          className={`${styles.tab}${mode === 'password' ? ` ${styles.tabActive}` : ''}`}
          onClick={() => switchMode('password')}
        >
          Password
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'magic'}
          className={`${styles.tab}${mode === 'magic' ? ` ${styles.tabActive}` : ''}`}
          onClick={() => switchMode('magic')}
        >
          Magic link
        </button>
      </div>

      <div style={{ marginTop: 22 }}>
        {mode === 'password' ? (
          <form className={styles.fields} onSubmit={(e) => submit('password', e)}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="login-email">Email</label>
              <Input
                id="login-email"
                size="large"
                type="email"
                autoComplete="email"
                placeholder="you@example.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldRow}>
                <label className={styles.label} htmlFor="login-password">Password</label>
                <Link className={styles.inlineLink} to="/forgot">
                  Forgot password?
                </Link>
              </div>
              <Input.Password
                id="login-password"
                size="large"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={busy}
              disabled={!email || !password || busy}
            >
              Sign in
            </Button>
          </form>
        ) : (
          <form className={styles.fields} onSubmit={(e) => submit('magic', e)}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="magic-email">Email</label>
              <Input
                id="magic-email"
                size="large"
                type="email"
                autoComplete="email"
                placeholder="you@example.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <span className={styles.hint}>
                We'll email you a link that signs you in. No password to remember.
              </span>
            </div>
            {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
            {magicSent && (
              <Alert
                type="success"
                showIcon
                message="Check your inbox"
                description={`If an account exists for ${email}, a sign-in link is on its way.`}
              />
            )}
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={busy}
              disabled={!email || busy}
            >
              Send magic link
            </Button>
          </form>
        )}
      </div>

      <p className={styles.altAction}>
        No password? <Link to="/login/otp">Sign in with a code</Link>
        <span style={{ margin: '0 6px', color: '#c7ccd6' }}>·</span>
        Never seen this before? Your invitation arrives by SMS.
      </p>
    </div>
  );
}

/**
 * Google's own mark, drawn inline per Google's sign-in branding guidelines.
 */
function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
